/** Client for the claim explorer API (task 4100.9). */
import { apiFetch } from "../../app/api";

const BASE = "/api/v1/claims";

export interface SourceAnchor {
  link_role: string;
  observation_id: string | null;
  source_region_id: string;
  region_key: string;
  region_kind: string;
  parsed_document_id: string;
  source_artifact_version_id: string;
  page_number: number | null;
  sheet_name: string | null;
  cell_ref: string | null;
}

export interface EvidenceSummary {
  items: number;
  by_standing: Record<string, number>;
  by_stance: Record<string, number>;
  channels: number;
  admitted_supporting_channels: number;
  admitted_contrary_channels: number;
  contradictions: number;
}

export interface ClaimRow {
  id: string;
  claim_type: string;
  lane: string;
  status: string;
  statement: string;
  target_object: string | null;
  target_attribute: string | null;
  mapping_rule_version_id: string | null;
  boundary: Record<string, unknown> | null;
  result_form: string | null;
  output_kind: string | null;
  evidence: EvidenceSummary | null;
  anchors: SourceAnchor[];
}

export interface ClaimListing {
  total: number;
  population: Record<string, number>;
  claims: ClaimRow[];
}

export interface ClaimQuery {
  engagement_id: string;
  run_id?: string;
  lane?: string;
  claim_type?: string;
  status?: string;
  dimension?: string;
  dimension_value?: string;
  limit?: number;
  offset?: number;
}

export function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

/** Empty filters are left off, so an unset filter never narrows the list. */
export function claimsPath(query: ClaimQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  return `${BASE}?${params.toString()}`;
}

export function listClaims(query: ClaimQuery): Promise<ClaimListing> {
  return apiFetch<ClaimListing>(claimsPath(query));
}

export function getClaim(claimId: string): Promise<ClaimRow> {
  return apiFetch<ClaimRow>(`${BASE}/${encodeURIComponent(claimId)}`);
}

/** Where a region sits, in the terms a reader looks for it. */
export function anchorText(anchor: SourceAnchor): string {
  const parts = [
    anchor.sheet_name && `sheet ${anchor.sheet_name}`,
    anchor.cell_ref && `cell ${anchor.cell_ref}`,
    anchor.page_number !== null && `page ${anchor.page_number}`,
    `${anchor.region_kind} ${anchor.region_key}`,
  ];
  return parts.filter(Boolean).join(", ");
}
